/**
 * „Was muss da sein" an einem Arbeitstag — Lieferung, Maschine, Besorgen, Hinweis
 * (Konzept Projekt-Tracking, § Was muss da sein). Dieselben Einträge wie die
 * Terminvorbereitung im Planer (`tages_event`), jetzt mit Art:
 *
 * - **Lieferung** kommt aus dem Material des Projekts. Statt eines eigenen Hakens
 *   gilt der Stand des Materials (angefragt, Angebot, bestellt, geliefert) — der
 *   Chip öffnet denselben Vergleich wie unter „Material & Lieferanten", die Anfrage
 *   nimmt das Datum dieses Tages als Wunschtermin.
 * - **Maschine** steht als eine Zeile am ersten Miettag, „bis Tag 3" (Benjamin, 02.10.2026).
 * - **Besorgen** hat einen Haken, **Hinweis** nur Text.
 */
import { useMemo, useState } from "react";
import { fehlerText } from "@vimaana/ui";

import Combobox from "../Combobox";
import AnfrageDialog, { type AnfrageStart } from "../projekt/AnfrageDialog";
import { StandChip, StandPanel, standVon } from "../projekt/AnfrageStand";
import { useBedarfAendern, useBedarfLoeschen, useBedarfNeu, useVorschlaegeAlle, useVorschlagAnnehmen, useVorschlagAus } from "../../hooks/useArbeitsplan";
import { useProjektMaterial } from "../../hooks/useLieferanten";
import { BEDARF_ARTEN, bedarfArt, tagName, type Arbeitsplan, type BedarfArt, type PlanBedarf, type PlanTag } from "../../lib/arbeitsplan";
import "../../styles/anfrage.css";
import "../../styles/lieferanten.css";

export default function BedarfListe({ plan, tag }: { plan: Arbeitsplan; tag: PlanTag }) {
  const projektId = plan.projekt_id;
  const neu = useBedarfNeu(projektId);
  const annehmen = useVorschlagAnnehmen(projektId);
  const alle = useVorschlaegeAlle(projektId);
  const ablehnen = useVorschlagAus(projektId);
  const aendern = useBedarfAendern(projektId);
  const loeschen = useBedarfLoeschen(projektId);
  const { data: materialDaten } = useProjektMaterial(projektId);
  const [art, setArt] = useState<BedarfArt>("lieferung");
  const [titel, setTitel] = useState("");
  const [uhrzeit, setUhrzeit] = useState("");
  const [offenId, setOffenId] = useState("");
  const [anfrage, setAnfrage] = useState<(AnfrageStart & { terminId?: string }) | null>(null);

  const materialien = materialDaten?.materialien || [];
  const spaetere = plan.tage.filter((t) => t.id !== tag.id && (t.plan_nr ?? 0) > (tag.plan_nr ?? 0));
  const fehler = neu.error || aendern.error || loeschen.error || annehmen.error || alle.error || ablehnen.error;
  const vorschlagLaeuft = annehmen.isPending || alle.isPending || ablehnen.isPending;
  // Was an diesem Tag geliefert wird und noch nicht angefragt ist — eine Anfrage für den ganzen Tag.
  const tagOffen = tag.bedarf
    .map((b) => materialien.find((x) => x.id === b.projekt_material_id))
    .filter((x): x is NonNullable<typeof x> => !!x && standVon(x).art === "offen");
  // Pro Material nur eine Lieferung je Tag vorschlagen.
  const schonDa = new Set(tag.bedarf.map((b) => b.projekt_material_id).filter(Boolean));
  const materialAuswahl = useMemo(() => materialien
    .filter((m) => !schonDa.has(m.id))
    .map((m) => ({
      id: m.id,
      label: m.name || "Material",
      zusatz: [m.menge && `${m.menge} ${m.einheit}`.trim(), m.lieferant_name].filter(Boolean).join(" · "),
    })), [materialien, schonDa]);

  const anlegen = (extra: Partial<Parameters<typeof neu.mutate>[0]> = {}) => {
    const text = (extra.titel ?? titel).trim();
    if (!text) return;
    neu.mutate(
      { termin_id: tag.id, kategorie: art, titel: text, uhrzeit: uhrzeit || undefined, ...extra },
      { onSuccess: () => { setTitel(""); setUhrzeit(""); } },
    );
  };
  const materialWaehlen = (id: string) => {
    const m = materialien.find((x) => x.id === id);
    if (!m) return;
    anlegen({ titel: [m.menge && `${m.menge} ${m.einheit}`.trim(), m.name].filter(Boolean).join(" "), projekt_material_id: m.id, lieferant_id: m.lieferant_id || undefined });
  };

  const zeile = (b: PlanBedarf) => {
    const a = bedarfArt(b.kategorie);
    const m = b.projekt_material_id ? materialien.find((x) => x.id === b.projekt_material_id) : undefined;
    return (
      <li key={b.id} className="apl-bedarf-zeile">
        <span className="apl-bedarf-zeichen" aria-hidden="true">{a.zeichen}</span>
        <div className="apl-bedarf-text">
          <strong className={b.erledigt ? "is-erledigt" : undefined}>{b.titel}</strong>
          <small>
            {[a.text, b.uhrzeit && `Anlieferung ${b.uhrzeit}`, b.lieferant_name,
              b.kategorie === "maschine" && b.bis_plan_nr ? `bis Tag ${b.bis_plan_nr}` : ""].filter(Boolean).join(" · ")}
          </small>
          {b.kategorie === "maschine" && spaetere.length > 0 && (
            <select
              className="apl-bedarf-bis"
              aria-label="Miete bis"
              value={b.bis_termin_id}
              onChange={(e) => aendern.mutate({ id: b.id, patch: { bis_termin_id: e.target.value } })}
            >
              <option value="">nur dieser Tag</option>
              {spaetere.map((t) => <option key={t.id} value={t.id}>bis {tagName(t)}</option>)}
            </select>
          )}
          {m && offenId === b.id && (
            <StandPanel m={m} projektId={projektId} onAnfrage={() => setAnfrage({ pmIds: [m.id], terminId: tag.im_kalender ? tag.id : undefined })} />
          )}
        </div>
        <div className="apl-bedarf-aktion">
          {m ? (
            <StandChip m={m} offen={offenId === b.id} onClick={() => setOffenId(offenId === b.id ? "" : b.id)} />
          ) : b.kategorie === "hinweis" ? null : (
            <label className="apl-haken" title="erledigt">
              <input type="checkbox" checked={b.erledigt} onChange={(e) => aendern.mutate({ id: b.id, patch: { erledigt: e.target.checked } })} />
              <span className="apl-sr">erledigt</span>
            </label>
          )}
          <button type="button" className="apl-x" aria-label={`${b.titel} löschen`} onClick={() => loeschen.mutate(b.id)}>×</button>
        </div>
      </li>
    );
  };

  return (
    <div className="apl-bedarf">
      <div className="apl-bedarf-kopf">
        <span className="apl-cap">Was muss da sein</span>
        {tagOffen.length > 0 && (
          <button type="button" className="vg-btn" title={tagOffen.map((x) => x.name).join(", ")}
            onClick={() => setAnfrage({ pmIds: tagOffen.map((x) => x.id), terminId: tag.im_kalender ? tag.id : undefined })}>
            Für diesen Tag anfragen ({tagOffen.length})
          </button>
        )}
      </div>
      {tag.vorschlaege.length > 0 && (
        <div className="apl-vorschlaege">
          <div className="apl-vorschlaege-kopf">
            <span>Aus dem Angebot vorgeschlagen</span>
            {tag.vorschlaege.length > 1 && (
              <button type="button" className="vg-btn" disabled={vorschlagLaeuft} onClick={() => alle.mutate(tag.id)}>Alle übernehmen</button>
            )}
          </div>
          <ul>
            {tag.vorschlaege.map((v) => (
              <li key={v.projekt_material_id} className="apl-vorschlag-zeile">
                <span className="apl-bedarf-zeichen" aria-hidden="true">{bedarfArt(v.kategorie).zeichen}</span>
                <div className="apl-bedarf-text">
                  <strong>{v.titel}</strong>
                  <small>
                    {[bedarfArt(v.kategorie).text, v.bis_plan_nr ? `bis Tag ${v.bis_plan_nr}` : "", v.position && `aus „${v.position}“`]
                      .filter(Boolean).join(" · ")}
                  </small>
                </div>
                <div className="apl-bedarf-aktion">
                  <button type="button" className="vg-btn vg-btn-primary" disabled={vorschlagLaeuft}
                    onClick={() => annehmen.mutate({ tagId: tag.id, pmId: v.projekt_material_id })}>Übernehmen</button>
                  <button type="button" className="vg-btn" disabled={vorschlagLaeuft} title="Wird nicht wieder vorgeschlagen"
                    onClick={() => ablehnen.mutate({ pmId: v.projekt_material_id })}>Nicht planen</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {tag.bedarf.length === 0 && <p className="apl-leise">Noch nichts — z. B. Lieferung Mineralgemisch, Rüttelplatte, 10 Sack Fugensand.</p>}
      {BEDARF_ARTEN.map((g) => {
        const liste = tag.bedarf.filter((b) => (bedarfArt(b.kategorie).art === g.art) || (g.art === "hinweis" && !BEDARF_ARTEN.some((x) => x.art === b.kategorie)));
        return liste.length ? <ul key={g.art} className="apl-bedarf-gruppe">{liste.map(zeile)}</ul> : null;
      })}

      <div className="apl-bedarf-neu">
        <div className="apl-arten" role="radiogroup" aria-label="Art">
          {BEDARF_ARTEN.map((a) => (
            <button key={a.art} type="button" role="radio" aria-checked={art === a.art}
              className={"apl-art" + (art === a.art ? " is-aktiv" : "")} onClick={() => setArt(a.art)}>
              <span aria-hidden="true">{a.zeichen}</span> {a.text}
            </button>
          ))}
        </div>
        {art === "lieferung" && materialAuswahl.length > 0 ? (
          <div className="apl-bedarf-eingabe">
            <Combobox wert="" eintraege={materialAuswahl} onWaehlen={materialWaehlen}
              platzhalter="Material des Projekts wählen" ariaLabel="Material für die Lieferung" disabled={neu.isPending} />
            <input type="time" value={uhrzeit} onChange={(e) => setUhrzeit(e.target.value)} aria-label="Anlieferung um" title="Anlieferung um (optional)" />
          </div>
        ) : (
          <div className="apl-bedarf-eingabe">
            <input
              value={titel}
              onChange={(e) => setTitel(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") anlegen(); }}
              placeholder={art === "maschine" ? "z. B. Rüttelplatte, Minibagger 1,5 t" : art === "besorgen" ? "z. B. 10 Sack Fugensand" : art === "hinweis" ? "z. B. Kundin lässt das Tor offen" : "Was wird geliefert?"}
              aria-label="Was muss da sein"
            />
            {(art === "lieferung" || art === "maschine") && (
              <input type="time" value={uhrzeit} onChange={(e) => setUhrzeit(e.target.value)} aria-label="Anlieferung um" title="Anlieferung um (optional)" />
            )}
            <button type="button" className="vg-btn vg-btn-primary" disabled={!titel.trim() || neu.isPending} onClick={() => anlegen()}>
              Hinzufügen
            </button>
          </div>
        )}
        {art === "lieferung" && materialien.length === 0 && (
          <p className="apl-leise">Material steht unter „Material &amp; Lieferanten“ — dort übernommen, ist es hier wählbar.</p>
        )}
      </div>
      {fehler && <p className="apl-fehler" role="alert">{fehlerText(fehler)}</p>}
      {anfrage && <AnfrageDialog projektId={projektId} start={anfrage} onClose={() => setAnfrage(null)} />}
    </div>
  );
}
